import type { MouseEvent, ReactNode } from 'react'
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { ICON_SM } from '../common/theme'

export interface TaActionButtonProps {
  // Tooltip text. Caller is responsible for picking the right text for the
  // current state (e.g. "Queued" vs "Generating…" vs "Generate Answer").
  title?: string
  // Click receives the MouseEvent so callers in nested-click contexts can
  // call `e.stopPropagation()` (e.g. row icons that mustn't bubble into the
  // row's own click handler).
  onClick?: (e: MouseEvent) => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void

  // Icon shown in the resting state. When `disabled` is true and no
  // `disabledIcon` is supplied, this same icon is rendered faded out.
  icon: ReactNode
  // Optional alternate icon shown when `disabled` is true (and not running).
  disabledIcon?: ReactNode

  // When true, replaces the icon with a CircularProgress and forces the
  // button into the disabled state so it cannot be clicked again.
  running?: boolean
  // When true, swaps the icon for the queued (clock) glyph and disables
  // the button. `running` wins if both are set - once the run starts the
  // spinner takes over.
  queued?: boolean
  disabled?: boolean

  // Pixel size of the underlying icon. The spinner is sized off this with
  // a 1.2× scaling factor so it visually fills the same slot the icon was
  // in, no matter what icon the caller passed. Defaults to ICON_SM (20).
  iconSize?: number

  // Forwarded onto the IconButton - useful for the gold "unsaved" tint.
  color?: string
  // sx merged into the IconButton's sx. Padding defaults to 0.375 to match
  // the dense toolbar buttons across the app.
  sx?: any
  testId?: string

  // Indicator mode - drops the IconButton wrapper (no click, no hover bg)
  // and just renders the icon/spinner badge. Use this inside tab headers
  // or wherever a click should be owned by the surrounding element.
  indicator?: boolean
}

// Compact toolbar action button used all over the question / project UI.
// Wraps an MUI IconButton with a Tooltip, a running spinner state, and an
// optional alternate-icon-when-disabled affordance. The Tooltip always wraps
// a <span> so it still fires even when the underlying button is disabled.
//
// In `indicator` mode the IconButton is dropped - only the icon/spinner
// badge is rendered, leaving click handling to the parent (tab header etc.).
export default function TaActionButton({
  title, onClick, onMouseEnter, onMouseLeave,
  icon, disabledIcon,
  running = false, queued = false, disabled = false,
  iconSize = ICON_SM,
  color, sx, testId,
  indicator = false,
}: TaActionButtonProps) {
  // Running wins over queued so the spinner takes over the moment the run
  // actually starts. queued-only renders the clock glyph and disables the
  // button just like running does.
  const showQueued = queued && !running
  const isDisabled = disabled || running || showQueued
  const restingIcon = isDisabled && disabledIcon ? disabledIcon : icon
  const spinnerSize = iconSize

  // Inner stack: resting icon kept mounted with visibility: hidden while
  // the running spinner or queued glyph is overlaid, so the button keeps
  // its natural width regardless of state.
  const stack = (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ display: 'inline-flex', visibility: (running || showQueued) ? 'hidden' : 'visible' }}>
        {restingIcon}
      </Box>
      {running && (
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CircularProgress size={spinnerSize} />
        </Box>
      )}
      {showQueued && (
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <AccessTimeIcon sx={{ fontSize: spinnerSize, color: 'text.secondary' }} />
        </Box>
      )}
    </Box>
  )

  // Indicator mode - no IconButton, no span-wrapping. Still apply a Tooltip
  // if the caller passed a title so users can hover for context.
  if (indicator) {
    const body = (
      <Box
        component="span"
        data-testid={testId}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          ...(color ? { color } : null),
          ...sx,
        }}
      >
        {stack}
      </Box>
    )
    return title ? <Tooltip title={title}>{body}</Tooltip> : body
  }

  const button = (
    <Box component="span" sx={{ display: 'inline-flex' }}>
      <IconButton
        size="small"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        disabled={isDisabled}
        data-testid={testId}
        sx={{ p: 0.375, ...(color ? { color } : null), ...sx }}
      >
        {stack}
      </IconButton>
    </Box>
  )
  return title ? <Tooltip title={title}>{button}</Tooltip> : button
}
